import { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';

const SYMBOLS = ['◆', '★', '▲', '●', '✦', '✚', '❖', '▼'];

const buildDeck = () => {
  const deck = [...SYMBOLS, ...SYMBOLS].map((symbol, i) => ({ id: i, symbol }));
  for (let i = deck.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [deck[i], deck[j]] = [deck[j], deck[i]];
  }
  return deck;
};

export function MemoryMatch({ onScore }) {
  const [deck, setDeck] = useState(buildDeck);
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState([]);
  const [moves, setMoves] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (done) return;
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [done]);

  useEffect(() => {
    if (flipped.length !== 2) return;
    const [a, b] = flipped;
    setMoves((m) => m + 1);
    if (deck[a].symbol === deck[b].symbol) {
      setMatched((m) => [...m, a, b]);
      setFlipped([]);
    } else {
      const t = setTimeout(() => setFlipped([]), 700);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flipped]);

  useEffect(() => {
    if (matched.length === deck.length && matched.length > 0 && !done) {
      setDone(true);
      const score = Math.max(10, 1000 - moves * 20 - elapsed * 5);
      onScore(score);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matched]);

  const reset = useCallback(() => {
    setDeck(buildDeck());
    setFlipped([]);
    setMatched([]);
    setMoves(0);
    setElapsed(0);
    setDone(false);
  }, []);

  const flip = (index) => {
    if (done || flipped.length === 2 || flipped.includes(index) || matched.includes(index)) return;
    setFlipped((f) => [...f, index]);
  };

  return (
    <div className="space-y-4" data-testid="game-memory-match">
      <div className="flex items-center justify-between text-sm">
        <span>Moves: <span className="tabular-nums" data-testid="memory-moves">{moves}</span></span>
        <span className="text-muted-foreground tabular-nums">{elapsed}s</span>
      </div>

      <div className="grid grid-cols-4 gap-2">
        {deck.map((card, index) => {
          const isOpen = flipped.includes(index) || matched.includes(index);
          return (
            <button
              key={card.id}
              type="button"
              onClick={() => flip(index)}
              className={`aspect-square rounded-lg border text-2xl flex items-center justify-center transition-all duration-300 ${
                isOpen
                  ? 'border-amber-400/50 bg-amber-400/15 text-amber-200'
                  : 'border-white/10 bg-white/5 text-transparent hover:border-white/25'
              }`}
              data-testid={`memory-card-${index}`}
            >
              {isOpen ? card.symbol : '?'}
            </button>
          );
        })}
      </div>

      {done && (
        <p className="text-center text-sm text-emerald-400" data-testid="memory-complete">
          Cleared in {moves} moves and {elapsed}s!
        </p>
      )}

      <Button variant="outline" className="rounded-full w-full" onClick={reset} data-testid="memory-restart">
        New board
      </Button>
    </div>
  );
}
